import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const API_BASE = (process.env.REACT_APP_API_BASE || '/api').replace(/["']/g, '').replace(/\/+$/, '');

// ✅ Helper to construct full image URL from database path
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const baseUrl = API_BASE.replace(/\/api\/?$/, '');
  return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
};

// ====================
// ✅ LaTeX helpers for MathText (Enhanced for e^(-x), complex fractions, etc.)
// ====================
const PAREN = '(?:\\((?:[^()]*|\\([^()]*\\))*\\)|\\[(?:[^\\[\\]]*|\\[[^\\[\\]]*\\])*\\])';
const TOKEN = '[A-Za-z0-9]+(?:\\s*\\^\\s*\\{?[A-Za-z0-9\\-]+\\}?)?';
const OPERAND = '(?:' + PAREN + '|' + TOKEN + ')';

const SUP_MAP = { '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8', '⁹': '9' };
const SUB_MAP = { '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4', '₅': '5', '₆': '6', '₇': '7', '₈': '8', '₉': '9' };

const MASTER = new RegExp(
  '(?<blank>_{3,})' +
  '|(?<intunicode>∫[₀-₉]+[⁰-⁹]+)' +
  '|(?<intlim>∫\\s*(?<ilo>\\d+)\\s*\\^\\s*(?<ihi>\\d+))' +
  '|(?<frac>(?<num>' + OPERAND + ')\\s*\\/\\s*(?<den>' + OPERAND + '))' +
  '|(?<intg>∫)' +
  '|(?<sup>[A-Za-z0-9]+\\s*\\^\\s*(?:\\{[^}]+\\}|\\([^)]+\\)|[A-Za-z0-9\\-]+))',
  'g'
);

function toLatexMath(s) {
  let out = String(s);
  
  // 1. Handle exponents: e^(-x) -> e^{-x}, x^{2} -> x^{2}, y^-3 -> y^{-3}
  out = out.replace(/\^\s*(?:\{([^}]+)\}|\(([^)]+)\)|([A-Za-z0-9\-]+))/g, (match, p1, p2, p3) => {
    const exp = p1 || p2 || p3;
    return `^{${exp}}`;
  });
  
  // 2. Handle unicode superscripts (e.g., x²)
  out = out.replace(/[⁰-⁹]+/g, (mm) => '^{' + [...mm].map((c) => SUP_MAP[c] || c).join('') + '}');
  
  // 3. Handle unicode subscripts for integrals (e.g., ∫₀¹)
  out = out.replace(/∫([₀-₉]+)([⁰-⁹]+)/g, (match, sub, sup) => {
    const lower = [...sub].map(c => SUB_MAP[c] || c).join('');
    const upper = [...sup].map(c => SUP_MAP[c] || c).join('');
    return `\\int_{${lower}}^{${upper}}`;
  });
  
  // 4. Math function names
  out = out.replace(/\b(ln|log|sin|cos|tan|cot|sec|csc|sqrt)\b/g, '\\$1');
  
  // 5. Common symbols
  out = out
    .replace(/±/g, '\\pm ')
    .replace(/×/g, '\\times ')
    .replace(/÷/g, '\\div ')
    .replace(/≤/g, '\\leq ')
    .replace(/≥/g, '\\geq ')
    .replace(/≠/g, '\\neq ')
    .replace(/→/g, '\\to ')
    .replace(/π/g, '\\pi ')
    .replace(/°/g, '^\\circ ');
    
  return out;
}

function stripOuter(s) {
  const t = String(s).trim();
  if ((t.startsWith('(') && t.endsWith(')')) || (t.startsWith('[') && t.endsWith(']'))) {
    return t.slice(1, -1);
  }
  return t;
}

function MathText({ children }) {
  const raw = children == null ? '' : String(children);
  const out = [];
  let last = 0;
  let key = 0;

  for (const m of raw.matchAll(MASTER)) {
    if (m.index > last) out.push(<span key={key++}>{raw.slice(last, m.index)}</span>);

    const g = m.groups || {};
    let latex = null;

    if (g.blank) {
      latex = '\\underline{\\hspace{1.2cm}}';
    } else if (g.intunicode) {
      latex = toLatexMath(g.intunicode);
    } else if (g.intlim) {
      latex = `\\int_{${g.ilo}}^{${g.ihi}}`;
    } else if (g.frac) {
      const num = g.num || '';
      const den = g.den || '';
      const looksMath = /\d/.test(num + den) || (num.length <= 4 && den.length <= 4);
      if (looksMath) {
        latex = `\\frac{${toLatexMath(stripOuter(num))}}{${toLatexMath(stripOuter(den))}}`;
      }
    } else if (g.intg) {
      latex = '\\int';
    } else if (g.sup) {
      latex = toLatexMath(g.sup);
    }

    if (latex) {
      out.push(
        <span
          key={key++}
          style={{ display: 'inline-block', margin: '0 2px', verticalAlign: 'middle' }}
          dangerouslySetInnerHTML={{ __html: katex.renderToString(latex, { throwOnError: false }) }}
        />
      );
    } else {
      out.push(<span key={key++}>{m[0]}</span>);
    }

    last = m.index + m[0].length;
  }
  if (last < raw.length) out.push(<span key={key++}>{raw.slice(last)}</span>);

  return (
    <span style={{ fontFamily: 'inherit', fontSize: '1.05em', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{out}</span>
  );
}

// ====================
// ✅ Beautiful modern styles
// ====================
const styles = {
  container: {
    maxWidth: 800,
    margin: '40px auto',
    padding: 0,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    padding: '24px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  stageBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.3)'
  },
  progressText: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: 500
  },
  progressBar: {
    height: 4,
    background: 'rgba(255,255,255,0.2)',
    width: '100%'
  },
  progressFill: {
    height: '100%',
    background: '#fff',
    transition: 'width 0.3s ease'
  },
  body: {
    padding: '32px'
  },
  questionText: {
    fontSize: 18,
    lineHeight: 1.6,
    color: '#2d3748',
    marginBottom: 24,
    fontWeight: 500
  },
  optionBtn: {
    width: '100%',
    padding: '16px 20px',
    marginBottom: 12,
    background: '#f7fafc',
    border: '2px solid #e2e8f0',
    borderRadius: 12,
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: 16,
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12
  },
  optionBtnHover: {
    background: '#edf2f7',
    borderColor: '#cbd5e0',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
  },
  optionSelected: {
    background: '#ebf8ff',
    borderColor: '#4299e1',
    boxShadow: '0 4px 12px rgba(66,153,225,0.2)'
  },
  optionLetter: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#e2e8f0',
    color: '#4a5568',
    fontWeight: 700,
    fontSize: 14,
    flexShrink: 0
  },
  optionLetterSelected: {
    background: '#4299e1',
    color: '#fff'
  },
  optionText: {
    flex: 1,
    paddingTop: 4
  },
  image: {
    maxWidth: '100%',
    maxHeight: 250,
    borderRadius: 12,
    marginBottom: 20,
    display: 'block',
    border: '1px solid #e2e8f0'
  },
  resultContainer: {
    textAlign: 'center',
    padding: '60px 32px'
  },
  resultTitle: {
    fontSize: 32,
    fontWeight: 700,
    marginBottom: 24,
    color: '#2d3748'
  },
  btnPrimary: {
    padding: '14px 32px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(102,126,234,0.3)',
    transition: 'all 0.2s ease'
  }
};

// ====================
// ✅ Token checker
// ====================
const checkAuth = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { valid: false, reason: 'token_missing' };
    return { valid: true, token };
  } catch (e) {
    return { valid: false, reason: 'storage_error' };
  }
};

export default function MathDiagnosticPlayer() {
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const assignmentId = searchParams.get('assignmentId');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [finalResult, setFinalResult] = useState(null);
  const [coreScore, setCoreScore] = useState(null);
  const [stage, setStage] = useState('core');
  const [difficulty, setDifficulty] = useState('basic');
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [totalQuestionsInBatch, setTotalQuestionsInBatch] = useState(0);
  
  // ✅ State to hold chapter tracking data from the backend
  const [sessionData, setSessionData] = useState(null);
  
  const answersRef = useRef([]);
  const submittingRef = useRef(false);
  const isCompletedRef = useRef(false);

  // ✅ FIX: Centralized "return to dashboard" that forces the dashboard to refetch
  const returnToDashboard = () => {
    window.dispatchEvent(new Event('dashboard_refresh'));
    navigate('/student/dashboard');
  };

  const fetchNextBatch = async (currentAnswers, effectiveAssignmentId, currentStage, currentDiff) => {
    if (submittingRef.current || isCompletedRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError('');

    const auth = checkAuth();
    if (!auth.valid) {
      navigate('/login', { replace: true });
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + auth.token
    };

    try {
      const res = await fetch(`${API_BASE}/student/math-diagnostic/${effectiveAssignmentId}/progress`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ stage: currentStage, difficulty: currentDiff, answers: currentAnswers })
      });

      if (res.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login', { replace: true });
        return;
      }

      if (res.status === 403) {
        const errData = await res.json().catch(() => ({}));
        setError(errData.error || 'You do not have access to this exam');
        return;
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server responded with ${res.status}`);
      }

      const data = await res.json();
      
      // ✅ Save the full session object to track chapters
      setSessionData(data.session);

      if (data.completed || data.session?.final) {
        isCompletedRef.current = true;
        setFinalResult(data.session.final);
        setCoreScore(data.session.core_score !== undefined && data.session.core_score !== null ? Number(data.session.core_score) : null);
        return;
      }

      setStage(data.session.stage);
      setDifficulty(data.session.difficulty);
      setCoreScore(data.session.core_score !== undefined && data.session.core_score !== null ? Number(data.session.core_score) : null);
      setQuestions(data.questions || []);
      setTotalQuestionsInBatch(data.session.total_questions_in_batch || data.questions?.length || 0);
      setCurrentIndex(0);
      setSelectedAnswer(null);
      answersRef.current = [];

    } catch (err) {
      console.error('❌ Fetch error:', err);
      setError(err.message || 'Failed to load questions');
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  useEffect(() => {
    if (!assignmentId) {
      setError('Invalid exam link. Please start the exam from your dashboard.');
      return;
    }
    
    const auth = checkAuth();
    if (!auth.valid) {
      navigate('/login', { replace: true });
      return;
    }
    
    fetchNextBatch([], assignmentId, 'core', 'basic');
  }, [assignmentId]);

  const handleOptionSelect = (option) => {
    if (selectedAnswer || loading) return;
    setSelectedAnswer(option);
    const q = questions[currentIndex];
    if (!q) return;

    const correctAnswer = q.correct_answer?.toString().toUpperCase().trim();
    const selectedOption = option.toString().toUpperCase().trim();
    const isCorrect = selectedOption === correctAnswer;

    answersRef.current.push({
      question_id: q.id,
      selected: selectedOption,
      concept_id: q.concept_id,
      concept: q.concept, 
      is_correct: isCorrect
    });

    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(prev => prev + 1);
        setSelectedAnswer(null);
      } else {
        fetchNextBatch(answersRef.current, assignmentId, stage, difficulty);
      }
    }, 600);
  };

  // ✅ Map the new stage names AND show chapter progress
  const getStageLabel = () => {
    const map = {
      core: '📊 Core Assessment',
      backup: '🔄 Backup Questions',
      supporting_basic: '🌱 Supporting Basic',
      supporting_intermediate: '📈 Supporting Intermediate',
      supporting_advanced: '🚀 Supporting Advanced'
    };
    const stageName = map[stage] || 'Diagnostic';
    
    // If backend sent chapter info, display it!
    if (sessionData?.current_chapter_name) {
      return `Ch ${sessionData.current_chapter_index + 1}/${sessionData.total_chapters}: ${sessionData.current_chapter_name} (${stageName})`;
    }
    
    return stageName;
  };

  const getProgressPercent = () => {
    if (totalQuestionsInBatch === 0) return 0;
    return ((currentIndex + 1) / totalQuestionsInBatch) * 100;
  };

  if (loading && questions.length === 0 && !finalResult) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.stageBadge}>Loading...</div>
        </div>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📐</div>
          <p style={{ color: '#718096', fontSize: 16 }}>Preparing your questions...</p>
        </div>
      </div>
    );
  }

  if (error && !finalResult) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.stageBadge}>Error</div>
        </div>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ color: '#e53e3e', marginBottom: 12 }}>Something went wrong</h2>
          <p style={{ color: '#718096', marginBottom: 24 }}>{error}</p>
          <button onClick={returnToDashboard} style={styles.btnPrimary}>
            ← Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ✅ Final Result Screen
  if (finalResult) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.stageBadge}>Exam Complete</div>
        </div>
        <div style={styles.resultContainer}>
          <h2 style={styles.resultTitle}>Exam Over!</h2>
          <p style={{ color: '#718096', fontSize: 16, marginBottom: 32 }}>
            Thank you for completing the exam.
          </p>
          <button onClick={returnToDashboard} style={styles.btnPrimary}>
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  if (!currentQ) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <p style={{ color: '#718096' }}>Loading next questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <div style={styles.stageBadge}>{getStageLabel()}</div>
          
          {coreScore !== null && coreScore !== undefined && !isNaN(coreScore) && stage !== 'core' && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 6 }}>
              Core Score: {Number(coreScore).toFixed(1)}%
            </div>
          )}
        </div>
        <div style={styles.progressText}>
          Question {currentIndex + 1} of {totalQuestionsInBatch || questions.length}
        </div>
      </div>
      
      <div style={styles.progressBar}>
        <div style={{ ...styles.progressFill, width: `${getProgressPercent()}%` }} />
      </div>

      <div style={styles.body}>
        <div style={styles.questionText}>
          <MathText>{currentQ.question_text}</MathText>
        </div>
        
        {currentQ.image_url && (
          <img 
            src={getImageUrl(currentQ.image_url)} 
            alt="Question"
            style={styles.image}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}

        <div>
          {['A', 'B', 'C', 'D'].map(opt => {
            const text = currentQ[`answer_${opt.toLowerCase()}`];
            if (!text) return null;
            const isSelected = selectedAnswer === opt;
            return (
              <button 
                key={opt} 
                disabled={!!selectedAnswer} 
                onClick={() => handleOptionSelect(opt)} 
                style={{
                  ...styles.optionBtn,
                  ...(isSelected ? styles.optionSelected : {})
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    Object.assign(e.currentTarget.style, styles.optionBtnHover);
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = styles.optionBtn.background;
                    e.currentTarget.style.borderColor = styles.optionBtn.border;
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={{
                  ...styles.optionLetter,
                  ...(isSelected ? styles.optionLetterSelected : {})
                }}>
                  {opt}
                </div>
                <div style={styles.optionText}>
                  <MathText>{text}</MathText>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', fontSize: 13, color: '#a0aec0', marginTop: 20 }}>
          Select an answer to continue
        </div>
      </div>
    </div>
  );
}